// A template's saves in the log (issue 1129; design/29 §7): the DM's row for each creature the spell caught — its
// working and outcome, a box for a save bonus the table could not read, and its damage proposed with Apply · Change ·
// Drop, as an attack's is (`DamageProposal`) — and a player's line, worked out on the server (`playerSave`); and the
// form on an area in the Areas list that starts a resolve (`ResolveSaves`).
import { AdvantageControl } from './AdvantageControl';
import type { AdvantageWord } from '@dragons-table/vtt-shared';
import { useState } from 'react';
import { caughtBy, elevation, overridePool,
  ON_SAVE, SAVE_LIMITS, dmSaveRows, tokenLabel, parseDamageText, parseSaveLine, playerSaveLines,
  type Area, type OnSave, type PlayerSave, type RollRecord, type SaveRow,
} from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { useMayAct } from '../../table/mayAct';
import { selection } from '../../board/selection';
import { BTN } from '../ui';

const btn = BTN;
const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs';
const effectWords: Record<OnSave, string> = { half: 'half damage on a success', none: 'no damage on a success', full: 'full damage on a success' };

/**
 * "Resolve saves…" on an area (issue 1129; design/29 §7): the DM's on any area, a player's on the area she laid — and
 * only where the table's system has saves and the area a rule for who it catches (issue 1305: hidden, not explained).
 * The form names the save, the DC and the spell's damage; the server lists who is caught, reads their bonuses and
 * throws every die. `label` is how the list names the area; the spell's name defaults to it.
 */
export function ResolveSaves({ area, label, extent }: { area: Area; label: string; extent: { width: number; height: number } }) {
  const { systemRules, meId } = useTableState();
  const [open, setOpen] = useState(false);
  const isDm = useMayAct()('area.resolve');
  if (systemRules.saves.length === 0 || area.includes === null || (!isDm && area.ownerId !== meId)) return null;
  return (
    <>
      <button type="button" className={btn} aria-label={`Resolve saves for ${label}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>Resolve saves…</button>
      {open && <ResolveSavesForm area={area} label={label} extent={extent} isDm={isDm} onClose={() => setOpen(false)} />}
    </>
  );
}

function ResolveSavesForm({ area, label, extent, isDm, onClose }: { area: Area; label: string; extent: { width: number; height: number }; isDm: boolean; onClose: () => void }) {
  const { systemRules, act, setNotice, store } = useTableState();
  const heightOf = (o: object) => elevation(o as { floor?: string; z?: number }, store.state.scene);
  // The doubled cover, by the word of the one resolving (`cover.improved`): the board cannot see an arrow slit.
  const [improved, setImproved] = useState<string[]>([]);
  // The form's word for a creature (docs/MRCS/dnd-5e.md §5.1), when the system's saves take it: a player's for their own.
  const [marked, setMarked] = useState<Record<string, AdvantageWord>>({});
  const saves = systemRules.saves;
  const [line, setLine] = useState('');
  const [save, setSave] = useState(saves[0]!.key);
  const [onSave, setOnSave] = useState<OnSave>('half');
  const [dc, setDc] = useState('');
  const [damage, setDamage] = useState('');
  const [name, setName] = useState(label);
  // The bag's Private starts on for the DM (the dice bag's default); a player's resolve is never private.
  const [priv, setPriv] = useState(true);

  const parsed = line.trim() ? parseSaveLine(line, saves) : null;
  const chosenSave = parsed ? parsed.save : save;
  // A line that leaves the effect to the DM ("Will partial") keeps the effect's choice open.
  const chosenEffect: OnSave = parsed && !parsed.leftToDm ? parsed.onSave : onSave;
  const dcN = Math.trunc(Number(dc));
  const dcOk = dc.trim() !== '' && Number.isFinite(dcN) && dcN >= 1 && dcN <= SAVE_LIMITS.dc;
  const typedDamage = damage.trim() ? parseDamageText(damage) : null;
  const badDamage = damage.trim() !== '' && !typedDamage;
  const ready = dcOk && !badDamage;
  const saveLabel = saves.find((s) => s.key === chosenSave)?.label ?? chosenSave;

  return (
    <form className="basis-full space-y-1 rounded border border-amber-700 p-2 text-xs" data-testid={`area-resolve-${area.id}`} onSubmit={(e) => {
      e.preventDefault();
      if (!ready) return;
      const body = {
        kind: 'area.resolve', areaId: area.id, save: chosenSave, onSave: chosenEffect, dc: dcN,
        ...(typedDamage ? { damage: typedDamage } : {}), private: isDm ? priv : false,
        ...(name.trim() ? { name: name.trim().slice(0, SAVE_LIMITS.name) } : {}),
        ...(systemRules.improvedSave?.save === chosenSave && improved.length ? { improved } : {}),
        ...(Object.keys(marked).length ? { advantage: marked } : {}),
      };
      act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'Those saves could not be rolled.'); else onClose(); });
    }}>
      {systemRules.saveAdvantage && caughtBy(area, Object.values(store.state.objects) as Parameters<typeof caughtBy>[1], store.state.grid, extent, heightOf)?.filter((o) => isDm || (o as { mine?: boolean }).mine).map((o) => (
        <div key={o.id} className="flex flex-wrap items-center gap-2">
          <span>{(store.state.objects as Record<string, { name?: string }>)[o.id]?.name}:</span>
          <AdvantageControl id={`save-${o.id}`} label={`Advantage on ${(store.state.objects as Record<string, { name?: string }>)[o.id]?.name}'s save`} value={marked[o.id] ?? null} onChange={(a) => { const { [o.id]: _was, ...rest } = marked; setMarked(a === 'normal' ? rest : { ...rest, [o.id]: a }); }} />
        </div>
      ))}
      {systemRules.improvedSave?.save === chosenSave && (
        <div className="flex flex-wrap items-center gap-2" data-testid={`area-improved-${area.id}`} title="The board works out cover from a wall; it cannot see an arrow slit. Tick who has the doubled cover.">
          <span>{systemRules.improvedSave.label}:</span>
          {caughtBy(area, Object.values(store.state.objects) as Parameters<typeof caughtBy>[1], store.state.grid, extent, heightOf)?.map((o) => (
            <label key={o.id} className="flex items-center gap-1">
              <input type="checkbox" checked={improved.includes(o.id)} onChange={(e) => setImproved(e.target.checked ? [...improved, o.id] : improved.filter((x) => x !== o.id))} />
              {(o as { name?: string }).name || 'Unnamed token'}
            </label>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1">
        <input aria-label="Name" maxLength={SAVE_LIMITS.name} className={`${field} w-28`} value={name} onChange={(e) => setName(e.target.value)} />
        <input aria-label="Save line" placeholder={`${saves[0]!.label} half`} maxLength={80} className={`${field} w-32`} value={line} onChange={(e) => setLine(e.target.value)} />
        <label className="flex items-center gap-1">DC <input aria-label="DC" type="number" min={1} max={SAVE_LIMITS.dc} className={`${field} w-12`} value={dc} onChange={(e) => setDc(e.target.value)} /></label>
        <label className="flex items-center gap-1">damage <input aria-label="Damage" placeholder="5d6" maxLength={80} className={`${field} w-24`} value={damage} onChange={(e) => setDamage(e.target.value)} /></label>
      </div>
      {parsed
        ? <p className="text-stone-400" data-testid={`area-resolve-reads-${area.id}`}>{saveLabel} save{parsed.leftToDm ? '; what a success does is yours to say' : `, ${effectWords[chosenEffect]}`}.</p>
        : (
          <div className="flex flex-wrap items-center gap-1">
            <select aria-label="Save" className={field} value={save} onChange={(e) => setSave(e.target.value)}>
              {saves.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        )}
      {(!parsed || parsed.leftToDm) && (
        <select aria-label="On a save" className={field} value={onSave} onChange={(e) => setOnSave(e.target.value as OnSave)}>
          {ON_SAVE.map((o) => <option key={o} value={o}>{effectWords[o]}</option>)}
        </select>
      )}
      {badDamage && <p className="text-amber-300">Damage reads like 5d6, or 2d6 + 3.</p>}
      <div className="flex flex-wrap items-center gap-1">
        <button type="submit" className={`${btn} border-amber-600`} disabled={!ready}>Resolve</button>
        <button type="button" className={btn} onClick={onClose}>Close</button>
        {isDm && (
          <label className="flex items-center gap-1 text-stone-300" title="Private: players see each save's outcome only.">
            <input type="checkbox" aria-label="Private" checked={priv} onChange={(e) => setPriv(e.target.checked)} /> Private
          </label>
        )}
      </div>
    </form>
  );
}

/** The DM's rows: one a creature, each with what is left for the DM to decide. */
export function SaveRows({ roll }: { roll: RollRecord }) {
  const { act, setNotice, store } = useTableState();
  if (!roll.save) return null;
  const label = roll.save.rules.save.label;
  // Each creature named as the turn order names it now (qa-runs 149): three "explore-token" rows said nothing of which.
  const rows = dmSaveRows(roll, (t) => tokenLabel({ id: t.id, name: t.name }, store.state.encounter) ?? t.name);
  // Every row's damage at once (the 2026-10-15 session pass): a burst over a pack of goblins was an Apply a goblin.
  // One save.apply a creature, as the rows send them; a row already changed or dropped is not touched.
  const open = rows.filter((r) => r.damage !== null && r.damage > 0 && !r.settled && !r.asks);
  const applyAll = () => {
    for (const r of open) act({ kind: 'save.apply', rollId: roll.id, target: r.id, amount: r.damage } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be saved.'); });
  };
  return (
    <>
      <ul className="mt-0.5" data-testid={`saves-${roll.id}`}>
        {rows.map((r) => <SaveRowEntry key={r.id} rollId={roll.id} row={r} label={label} name={r.name || 'it'} />)}
      </ul>
      {open.length > 1 && <button type="button" className={`${btn} mt-0.5 font-sans`} onClick={applyAll}>Apply all {open.length}</button>}
    </>
  );
}

function SaveRowEntry({ rollId, row, label, name }: { rollId: string; row: SaveRow; label: string; name: string }) {
  const { act, setNotice, store, systemRules } = useTableState();
  // The DM's button on a failed save (`save_override`), with a use left.
  const override = row.result === 'failed' && !row.settled ? overridePool((store.state.encounter as { combatants: Parameters<typeof overridePool>[0] }).combatants, row.id, systemRules.pools) : null;
  const [bonus, setBonus] = useState('');
  const [changing, setChanging] = useState(false);
  const [value, setValue] = useState('');
  const send = (body: Record<string, unknown>) => act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be saved.'); });
  const apply = (amount: number | null) => send({ kind: 'save.apply', rollId, target: row.id, amount });
  const open = row.damage !== null && row.damage > 0 && !row.settled;
  return (
    <li className="whitespace-pre-wrap break-words" data-testid={`save-row-${row.id}`} data-result={row.result}>
      {/* The name selects its token on the board, to see which it is. */}
      <button type="button" className="underline decoration-dotted hover:text-amber-200" title="Select it on the board" onClick={() => selection.only(row.id)}>{row.name}</button>
      {row.line.slice(row.name.length)}
      {!row.bonusKnown && row.result === 'unresolved' && (
        <form className="mt-0.5 flex items-center gap-1 font-sans" onSubmit={(e) => { e.preventDefault(); const n = Math.trunc(Number(bonus)); if (bonus.trim() !== '' && Number.isFinite(n)) send({ kind: 'save.bonus', rollId, target: row.id, bonus: n }); }}>
          <span className="text-amber-300">{name}'s {label} bonus?</span>
          <input aria-label={`${name}'s ${label} bonus`} type="number" className="w-14 rounded bg-stone-800 px-1 py-0.5 text-xs" value={bonus} onChange={(e) => setBonus(e.target.value)} />
          <button type="submit" className={btn}>Set</button>
        </form>
      )}
      {override && <button type="button" className={`${btn} mt-0.5 font-sans`} title={`Spends one of ${override.label}: this save succeeds.`} onClick={() => send({ kind: 'save.override', rollId, target: row.id })}>{override.label} ({override.combatant.pools![override.key]!.left})</button>}
      {open && (
        <div className="mt-0.5 flex flex-wrap items-center gap-1 font-sans">
          <button type="button" className={btn} aria-label={`Apply ${row.damage} damage to ${name}`} title={`Apply ${row.damage} damage to ${name}`} onClick={() => apply(row.damage)}>Apply {row.damage}</button>
          {!changing && <button type="button" className={btn} onClick={() => { setValue(String(row.damage)); setChanging(true); }}>Change…</button>}
          {changing && (
            <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); const n = Math.trunc(Number(value)); if (value.trim() !== '' && Number.isFinite(n) && n >= 0) apply(n); }}>
              <input aria-label={`Damage to apply to ${name}`} type="number" min={0} className="w-14 rounded bg-stone-800 px-1 py-0.5 text-xs" value={value} onChange={(e) => setValue(e.target.value)} />
              <button type="submit" className={btn}>Apply</button>
            </form>
          )}
          <button type="button" className={btn} aria-label={`Drop the damage to ${name}`} onClick={() => apply(null)}>Drop</button>
        </div>
      )}
    </li>
  );
}

/** A player's line for a template's saves: what they may know of it, in the caster's colour. */
export function PlayerSaveEntry({ save, colour }: { save: PlayerSave; colour: string }) {
  const [head, ...rest] = playerSaveLines(save);
  return (
    <li className="vtt-log-card min-w-0 text-xs" style={{ borderLeftColor: colour }} data-testid={`save-${save.id}`}>
      <div className="whitespace-pre-wrap break-words">
        <span style={{ color: colour }} className="font-semibold">{head}</span>
        {rest.length > 0 && <span className="text-stone-400">{`\n${rest.join('\n')}`}</span>}
      </div>
    </li>
  );
}
